// Language context provider - v3 cache bust
import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from "react";

export type Language = "en" | "fr";

const STORAGE_KEY = "acquiz_lang";

function getStoredLanguage(): Language {
  if (typeof window === "undefined") return "en";
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "en" || stored === "fr" ? stored : "en";
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, any>> = {
  en: {
    nav: {
      services: "Services",
      sellers: "For Sellers",
      buyers: "For Buyers",
      consultants: "For Consultants",
      brokers: "For Brokers",
      internal_partners: "Internal Partners",
      lenders: "Lenders",
      business_partners: "Business Partners",
      opportunities_hub: "Opportunities Hub",
      resources: "Resources",
      for_sale: "For Sale",
      buyer_plan: "Buyer Plans",
      seller_plan: "Seller & Broker Plans",
      deal_monitor: "Deal Monitor",
      blog: "Blog",
      elearning: "E-Learning",
      faq: "FAQ",
      about: "About",
      our_experts: "Our Experts",
      business_immigration: "Business Immigration",
      notary: "Notary",
      tax_advisor: "Tax Advisor",
    },
    cta: {
      join_ecosystem_short: "Join Ecosystem",
      join_ecosystem_title: "Join the AcquizGroup Ecosystem",
      join_ecosystem_desc: "Become part of our network of lenders and business partners.",
      i_am_lender: "I am a Lender",
      i_am_internal_partner: "I am a Business Partner",
    },
    index: {
      hero: {
        title: "Connecting Ambition to Action",
        subtitle: "The first integrated platform for acquiring, financing, and transferring high-potential businesses, all within a secure ecosystem.",
        im_seller: "I'm a Seller",
        im_buyer: "I'm a Buyer",
        im_consultant: "I'm a Consultant",
        im_broker: "I'm a Broker",
        im_lender: "I'm a Lender",
        im_internal_expert: "I'm an Internal Expert",
      },
      financing: {
        title: "Redefining Acquisition Financing",
        subtitle: "Because the success of a business transfer depends on impeccable data, we have built a financing standard that places transparency and performance at the heart of every transaction.",
        box1_title: "Expanding Access, Reducing Bias",
        box1_desc: "We are democratizing financing by eliminating traditional barriers and allocation biases that hinder promising transactions. Our priority: speed and fairness.",
        box2_title: "Clarity Through Numbers",
        box2_desc: "The viability of a project should not be a mystery. We base our analyses on undeniable fundamentals, such as profit margin and a DSCR > 1.3, guaranteeing the quality of each application.",
        box3_title: "Performance Without Prospecting",
        box3_desc: "As a true engine of opportunity, we connect lenders to a stream of highly qualified clients. We optimize the ecosystem without ever interfering in final credit decisions.",
        learn_more: "Learn More",
      },
      ecosystem: {
        title: "The AcquizGroup Ecosystem",
        subtitle: "Discover the right path for you based on your profile.",
        seller: "Seller",
        seller_desc: "Maximize your business value through the pre-financing process, **track the interest your business generates**, find the ideal buyer, and **receive contact requests** through our intelligent platform. Through our Consultant program, discover the **experts and professionals** you need to create the best selling conditions.",
        sell_cta: "Sell My Business",
        buyer: "Buyer",
        buyer_desc_1: "Never miss another high-potential opportunity.",
        buyer_desc_2: "Benefit from our network of consultants and create winning partnerships.",
        buyer_desc_3: "Structure your acquisition plan from financing to integration through our intelligent platform.",
        buy_cta: "Buy a Business",
        consultant: "Consultant",
        consultant_desc_1: "Your **Know-How and Licences** are worth gold.",
        consultant_desc_2: "Offer your services **for remuneration or a share of equity** to business owners or buyers.",
        consultant_desc_3: "Create partnerships by sharing your expertise, skill sets, licenses, and more.",
        consultant_desc_4: "Become a key element in the business acquisition and transfer process.",
        consultant_cta: "Become a Consultant",
        lender: "Lender",
        lender_desc:
          "Receive a steady stream of pre-qualified deal opportunities that match your lending criteria. Access complete, verified deal files and make faster, better-informed credit decisions.",
        lender_cta: "Become a Lender",
        broker: "Broker",
        broker_desc:
          "Manage your clients' transactions end to end from a single workspace. Submit and track deals, **add the sellers you represent**, receive contact requests and buyer profile details jointly, and **coordinate your calendar with your clients'**. **Breathe new life into your dormant deals.** Streamline every step of the process.",
        broker_cta: "Become a Broker",
        closing_expert: "Closing Expert",
        closing_expert_desc:
          "**Whether you are a business-law or entrepreneurial-immigration lawyer, a notary, a tax advisor, or an accountant**, offer your services directly on every platform user's dashboard and/or directly on the listings of businesses for sale in your service area.",
        closing_expert_cta: "Become a Closing Expert",
      },
      why: {
        title: "Why Choose AcquizGroup?",
        subtitle: "In a market where business transition is a major challenge, we offer you operational precision and human listening to make your transaction a success.",
        card1_title: "Human & Personalized Approach",
        card1_desc: "We know every transaction is unique. At AcquizGroup, we believe a successful transaction relies on the perfect balance between technology efficiency and human warmth. AcquizGroup is digital power, enriched by dedicated professional support.",
        card2_title: "Technological Innovation",
        card2_desc: "AcquizGroup offers more than a simple platform: we provide a trusted ecosystem that helps you achieve your goals with peace of mind, saving you time and money. Thanks to this technological innovation, you end scattered email exchanges, Dropbox, Zoom, etc. Everything is now integrated on one platform: document sharing, calendar, video conference, transcription, file verification and tracking.",
        card3_title: "Reliable Partner Network",
        card3_desc: "Access a wide directory of qualified experts in every business sector and industry. Because your transaction security is our absolute priority, each consultant in our network undergoes a rigorous verification process to guarantee service integrity and prevent fraud.",
      },
      newsletter: {
        title: "Stay Updated",
        subtitle: "Subscribe to our newsletter for the latest deals, insights, and market trends.",
        placeholder: "Enter your email",
        subscribe: "Subscribe",
        subscribing: "Subscribing...",
      },
      partner_cta: {
        title: "Join Our Ecosystem",
        subtitle: "Are you a lender or business partner? Join our trusted network and access exclusive opportunities.",
        im_lender: "I'm a Lender",
        im_partner: "I'm a Business Partner",
      },
      footer: {
        about: "About",
        about_desc: "We facilitate partnerships, business transmission, and operational structuring.",
        transactions: "Transactions",
        sell: "Sell",
        buy: "Buy",
        create_listing: "Create Listing",
        partners: "Partners",
        consultants: "Consultants",
        partners_link: "Partners",
        lenders: "Lenders",
        resources: "Resources",
        deal_monitor: "Deal Monitor",
        contact: "Contact",
        contact_desc: "Discuss your acquisition or transmission projects.",
        contact_team: "Contact The Team",
        terms: "Terms & Conditions",
        privacy: "Privacy Policy",
        cookies: "Cookie Policy",
        copyright: "© 2026 AcquizGroup. All rights reserved.",
      },
      sign_in: "Sign In",
      business_lawyers: "Business Lawyers",
      immigration_lawyers: "Immigration Lawyers",
    },
    footer: {
      about_title: "About AcquizGroup",
      about_description: "We are facilitators of partnerships, business transfers and operational structuring. Our mission is to connect ambition with action.",
      services: "Key Allies",
      sell_business: "Sell a Business",
      buy_business: "Buy a Business",
      become_partner: "Our Experts",
      resources: "Resources",
      contact: "Contact",
      contact_description: "Contact us to discuss your acquisition or transfer projects.",
      contact_us: "Contact The Team",
      copyright: "© 2024 AcquizGroup. All rights reserved.",
      terms: "Terms & Conditions",
      privacy: "Privacy Policy",
      cookies: "Manage Cookies",
      cookies_policy: "Cookie Policy",
    },
    listings: {
      tagline: "KEEP GROWING WITH US",
      sign_in: "Sign In",
      cash_flow: "Cash Flow",
      pre_financing_offers: "pre-financing offers",
      add_favorite: "Add to favorites",
      remove_favorite: "Remove from favorites",
      added_to_favorites: "Added to favorites ❤️",
      removed_from_favorites: "Removed from favorites",
    },
    admin: {
      nav: {
        dashboard: "Dashboard",
        users: "Users",
        companies: "Companies",
        deals: "Deals",
        submissions: "Submissions",
        messages: "Messages & Chat",
        calls: "Calls & Meetings",
        documents: "Documents",
        partners: "Partners",
        operations: "Operations",
        calendar: "Calendar",
        finance: "Finance",
        kpis: "KPIs",
      },
      dashboard: {
        title: "Administration Portal",
        create_deal: "Create Deal",
        view_all: "View All",
        users_card: "Users",
        deals_card: "Deals",
        submissions_card: "Submissions",
        no_users: "No users yet",
        no_deals: "No deals yet",
        no_submissions: "No submissions",
      },
    },
    broker_registration: {
      page_title: "Broker Registration Questionnaire",
      steps: {
        name: "Step",
        of: "of",
        profile: "Profile",
        expertise: "Expertise",
        model: "Model",
        methodology: "Methodology",
        compliance: "Compliance",
      },
      step_one: {
        title: "1. Broker Profile",
        first_name: "First Name",
        first_name_placeholder: "e.g., John",
        last_name: "Last Name",
        last_name_placeholder: "e.g., Doe",
        firm_name: "Brokerage Firm Name",
        firm_name_placeholder: "e.g., Doe Business Brokers Inc.",
        email: "Email Address",
        email_placeholder: "e.g., john@doebusinessbrokers.com",
        phone: "Phone Number",
        phone_placeholder: "e.g., +1-555-123-4567",
        password: "Password",
        password_placeholder: "Enter your password",
        confirm_password: "Confirm Password",
        confirm_password_placeholder: "Confirm your password",
        address_type: "Address Type",
        address_type_residential: "Residential",
        address_type_corporate: "Corporate",
        address_search: "Full Address Search",
        address_search_placeholder: "Type your address to search",
        street_address: "Full Street Address",
        street_address_placeholder: "123 Main Street, Suite 400",
        city: "City",
        city_placeholder: "e.g., Toronto",
        state_province: "State/Province",
        state_province_placeholder: "e.g., Ontario",
        zip_postal_code: "Zip/Postal Code",
        zip_postal_code_placeholder: "e.g., M5H 2N2",
        country: "Country",
        mother_tongue: "Mother Tongue",
        spoken_languages: "Other Spoken Languages",
        spoken_languages_hint: "(Check all that apply)",
        profile_enhancement: "Profile Enhancement (Optional)",
        profile_photo: "Profile Photo",
        photo_hint: "A professional photo helps build trust with sellers and buyers. Max 5MB, JPG or PNG.",
        upload_photo: "Upload Photo",
        change_photo: "Change Photo",
        professional_description: "Professional Description",
        description_hint: "Describe your experience and what makes you a trusted broker (optional)",
        description_placeholder: "Share your professional background, specializations, years of experience, and why clients should work with you...",
      },
      step_two: {
        title: "2. Expertise & Licenses",
        subtitle: "Areas of expertise and professional certifications",
        specializations_title: "Areas of Expertise",
        add_specialization: "Add an Area",
        primary_sector: "Primary Sector",
        primary_sector_placeholder: "Select a sector",
        years_experience: "Years of Experience",
        years_experience_placeholder: "Select your experience",
        expertises: "Expertise Types",
        expertises_hint: "(Check all that apply)",
        licenses_title: "Professional Licenses",
        add_license: "Add a License",
        license_details: "License Details",
        license_details_placeholder: "e.g., Certified Business Intermediary (CBI)",
        license_number: "License Number",
        license_number_placeholder: "e.g., CBI-12345",
        jurisdictions: "Jurisdictions",
        jurisdictions_hint: "(Check all that apply)",
        other_jurisdictions: "Other Jurisdictions",
        other_jurisdictions_placeholder: "Specify other jurisdictions if applicable",
      },
      step_three: {
        title: "3. Transaction Model",
        subtitle: "Your transaction approach and agreements",
        transaction_types: "Transaction types you facilitate",
        transaction_types_hint: "(Check all that apply)",
        nda_enforcement: "Documents produced by the platform for compliance",
        nda_enforcement_hint: "By checking these boxes you agree to the following:",
        nda_prefinancing_share: "AcquizGroup will share with platform lenders the information related to the businesses you submit for the pre-financing stage.",
        nda_standardized_documents: "To ensure consistency across the entire platform, AcquizGroup will use its own NDA, CIM and LOI documents.",
        nda_positive_cashflow: "Only businesses with positive cash flow will be accepted.",
        nda_evaluation: "AcquizGroup will conduct its own evaluation of the businesses provided.",
      },
      step_four: {
        title: "4. Methodology (KPIs)",
        subtitle: "Key performance indicators and criteria",
        post_sale_role: "Post-sale role",
        post_sale_role_hint: "(Check all that apply)",
        vetting_process: "Proof of Funds (PoF) requirements",
        vetting_process_hint: "(Check all that apply)",
        cash_down_min_percent: "Minimum down payment percentage",
        fixed_amount_min: "Minimum liquid amount",
        fixed_amount_max: "Maximum liquid amount",
        accepted_pof_types: "Accepted PoF types",
        accepted_pof_types_hint: "(Check all that apply)",
        communication_style: "Preferred communication style",
        communication_style_hint: "(Check all that apply)",
        competing_offers: "Competing offers management",
        competing_offers_hint: "(Check all that apply)",
        deal_size_min: "Minimum deal size",
        deal_size_max: "Maximum deal size",
        fee_structure: "Fee structure",
        fee_structure_hint: "(Check all that apply)",
      },
      step_five: {
        title: "5. Compliance and Consent",
        subtitle: "Terms and conditions, background check",
        terms_title: "Terms and Conditions",
        terms_checkbox: "I have read and accept the terms and conditions above",
        background_check_title: "Background Check",
        background_check_content: "AcquizGroup conducts background checks on all registered brokers to ensure platform safety and compliance.\n\nThis check may include:\n• Identity verification\n• Criminal background check\n• Professional reference verification\n• License and certification validation\n\nAll information is handled confidentially and in compliance with applicable data protection laws.",
        background_check_checkbox: "I formally authorize AcquizGroup to conduct a background check and confirm that all information provided is accurate",
      },
      navigation: {
        previous: "← Previous",
        next: "Next →",
        submit: "Submit Questionnaire",
        submitting: "Submitting...",
      },
      success: {
        title: "Registration Submitted Successfully!",
        message: "Thank you for completing the broker registration form.",
        next_steps: "Next steps:",
        step_1: "You will receive a confirmation email shortly",
        step_2: "Once confirmed, you will have access to your dashboard",
        step_3: "",
        redirecting: "Redirecting to dashboard in",
        seconds: "seconds",
      },
      already_member: "Already a member?",
      login: "Login",
      errors: {
        broker_name_required: "Broker name is required",
        firm_name_required: "Firm name is required",
        email_required: "Email address is required",
        email_invalid: "Invalid email format",
        phone_required: "Phone number is required",
        street_address_required: "Street address is required",
        city_required: "City is required",
        state_province_required: "State/Province is required",
        zip_postal_code_required: "Zip/Postal code is required",
        country_required: "Country is required",
        mother_tongue_required: "Mother tongue is required",
        spoken_languages_required: "At least one other language must be selected",
        specializations_required: "At least one area of expertise is required",
        industry_required: "Sector is required",
        expertises_required: "At least one expertise type must be selected",
        experience_required: "Years of experience is required",
        licenses_required: "At least one license is required",
        license_details_required: "License details are required",
        license_number_required: "License number is required",
        jurisdictions_required: "At least one jurisdiction must be selected",
        transaction_types_required: "At least one transaction type must be selected",
        evaluation_process_required: "At least one evaluation process must be selected",
        nda_enforcement_required: "NDA enforcement is required",
        post_sale_role_required: "At least one post-sale role must be selected",
        vetting_process_required: "At least one PoF requirement must be selected",
        cash_down_percent_required: "Down payment percentage is required when option is checked",
        fixed_amounts_required: "Minimum and maximum amounts are required when option is checked",
        accepted_pof_types_required: "At least one PoF type must be selected",
        communication_style_required: "At least one communication style must be selected",
        competing_offers_required: "Competing offers management is required",
        deal_size_min_required: "Minimum deal size is required",
        deal_size_max_required: "Maximum deal size is required",
        fee_structure_required: "At least one fee structure must be selected",
        terms_required: "You must accept the terms and conditions",
        background_check_required: "You must accept the background check",
      },
      options: {
        countries: {
          CA: "Canada",
          US: "United States",
          FR: "France",
          UK: "United Kingdom",
          OTHER: "Other",
        },
        address_types: {
          residential: "Residential",
          corporate: "Corporate",
        },
        languages: {
          French: "French",
          English: "English",
          Spanish: "Spanish",
          German: "German",
          Italian: "Italian",
          Portuguese: "Portuguese",
          Russian: "Russian",
          Mandarin: "Mandarin",
          Arabic: "Arabic",
          Japanese: "Japanese",
          Other: "Other",
        },
        industries: {
          all: "All",
          agriculture_food: "Agriculture, Agri-Food & Food Industry",
          automotive_marine: "Automotive & Boat / Marine",
          beauty_personal: "Beauty & Personal Care",
          construction: "Building & Construction (incl. Trades & HVAC)",
          communication_media: "Communication, Media & Public Relations",
          education: "Education & Children Services",
          entertainment_sport: "Entertainment, Recreation & Sport",
          financial_insurance: "Financial Services & Insurance",
          healthcare: "Health Care, Medical & Life Sciences",
          manufacturing: "Manufacturing & Industrial Production",
          tech_saas: "Online & Technology (SaaS, Web Dev, IT Services)",
          pet_services: "Pet Services",
          restaurant_food: "Restaurants & Food Service",
          retail: "Retail (General, Sport, Specialized)",
          service_general: "Service Businesses (General)",
          transportation: "Transportation, Trucking & Logistics",
          travel_tourism: "Travel, Tourism & Hospitality",
          wholesale: "Wholesale & Distributors",
          accounting_tax: "Accounting & Tax Firm (CPA, CA)",
          legal_firm: "Legal Firm (Corporate, IP, Real Estate)",
          engineering_consulting: "Engineering & Technical Consulting (Civil, Electrical, Chemical, Aero)",
          real_estate: "Real Estate Services & Property Management",
          waste_environmental: "Waste Management, Water Treatment & Environmental",
          business_intelligence: "Business Intelligence & Data Consulting",
          recruitment_hr: "Recruitment & Human Resources",
        },
        expertises: {
          financial_analysis: "Financial analysis",
          valuation: "Business valuation",
          legal_due_diligence: "Legal due diligence",
          market_research: "Market research",
          negotiation: "Negotiation",
        },
        experience_years: {
          "0-5": "0 to 5 years",
          "6-10": "6 to 10 years",
          "10+": "10+ years",
        },
        transaction_types: {
          asset_purchase: "Asset Purchase",
          share_purchase: "Share Purchase",
          merger: "Merger",
          partnership: "Partnership / Joint Venture",
        },
        evaluation_process: {
          credit_check: "Credit Check",
          financial_review: "Financial Review",
          industry_experience: "Industry Experience Review",
          interview: "In-Person Interview",
        },
        nda_enforcement: {
          mandatory_all: "Mandatory for all buyers before disclosure",
          mandatory_financials: "Mandatory before financial disclosure only",
          case_by_case: "Case-by-case basis",
          not_required: "Not systematically required",
        },
        post_sale_role: {
          transition_support: "Transition Support",
          ongoing_advisory: "Ongoing Advisory (6-12 months)",
          none: "None - Transaction only",
        },
        pof_requirements: {
          cash_down_payment: "Cash Down Payment (% of purchase price)",
          fixed_liquid_amount: "Fixed Liquid Amount (independent of price)",
          pre_approval_letter: "Lender Pre-Approval Letter",
          no_pof_required: "No PoF required upfront",
        },
        cash_down_percent: {
          "10": "10%",
          "20": "20%",
          "30": "30%",
          "40": "40%+",
        },
        fixed_amounts: {
          "50k": "$50k",
          "100k": "$100k",
          "250k": "$250k",
          "500k": "$500k+",
          "1M+": "$1M+",
        },
        pof_types: {
          bank_statement: "Bank Statement (3 months)",
          bank_letter: "Bank Comfort Letter",
          investment_statement: "Investment Account Statement",
          lender_preapproval: "Lender Pre-Approval",
        },
        communication_style: {
          phone: "Phone Calls",
          email: "Email",
          video: "Video Conferencing",
          in_person: "In-Person Meetings",
        },
        competing_offers: {
          transparent_disclosure: "Transparent disclosure to all parties",
          blind_bidding: "Blind bidding process",
          best_and_final: "Best and Final offer round",
          first_come: "First come, first served",
        },
        deal_sizes: {
          under_500k: "Under $500k",
          "500k_1M": "$500k - $1M",
          "1M_2M": "$1M - $2M",
          "2M_5M": "$2M - $5M",
          "5M_10M": "$5M - $10M",
          "10M_25M": "$10M - $25M",
          "25M_50M": "$25M - $50M",
          "50M_plus": "$50M+",
        },
        fee_structure: {
          success_only: "Success Fee Only",
          retainer_plus_success: "Retainer + Success Fee",
          hourly: "Hourly Rate",
          flat_fee: "Flat Fee",
        },
      },
    },
    consultant_registration: {
      page_title: "Consultant Registration Questionnaire",
      steps: {
        name: "Step",
        of: "of",
        account: "Account",
        profile: "Profile",
        services: "Services",
        compliance: "Compliance",
      },
      step_one: {
        title: "1. Account Information & Residential Address",
        personal_info: "Personal Information",
        full_name: "Full Name",
        full_name_placeholder: "e.g., Marie Dubois",
        email: "Email Address",
        email_placeholder: "you@firm.com",
        phone: "Phone Number",
        phone_placeholder: "+1 514-...",
        institution_types: "Institution Type(s) / Primary Role",
        institution_types_hint: "Select one or more types (multiple selection allowed)",
        languages: "Languages",
        mother_tongue: "Mother Tongue",
        mother_tongue_placeholder: "— Select your mother tongue —",
        spoken_languages: "Other Spoken Languages (optional)",
        spoken_languages_hint: "Select all languages you speak fluently",
        address_title: "Current Residential Address",
        address_subtitle: "Required for background check (Background Check)",
        address_search: "Address Search",
        address_search_placeholder: "Start typing your address...",
        address_search_hint: "Type your address and select it from the dropdown list",
        street_address: "Full Address (Street, Apt/Unit)",
        street_address_placeholder: "123 Main St, Apt 4",
        street_address_hint: "Automatically filled by search, but manually editable",
        city: "City",
        city_placeholder: "Miami",
        state_province: "State/Province",
        state_province_placeholder: "FL",
        zip_postal_code: "Zip/Postal Code",
        zip_postal_code_placeholder: "33101",
        country: "Country",
        country_placeholder: "USA",
        account_security: "Account Security",
        password: "Password",
        password_placeholder: "Min. 6 characters",
        confirm_password: "Confirm Password",
        confirm_password_placeholder: "Re-enter your password",
      },
      step_two: {
        title: "2. Profile & Expertise",
        errors_summary: "⚠️ Please correct the following errors before continuing:",
        company_name: "Company Name (optional)",
        company_name_placeholder: "Your firm (if applicable)",
        sectors_title: "Business Sectors & Expertises",
        sectors_subtitle: "Select up to 3 business sectors for which you would like to promote your services or in which you would like to create a partnership",
        sector: "Sector",
        remove: "Remove",
        business_sector: "Business Sector",
        select_sector: "— Select a sector —",
        key_expertises: "Key Related Expertise (multiple selection)",
        key_expertises_hint: "Select at least one expertise",
        years_experience: "Years of Experience in this Sector",
        select_experience: "— Select —",
        add_sector: "Add a business sector ({{remaining}} remaining)",
        licenses_title: "Licenses & Permits",
        has_license: "Do you hold active professional licenses?",
        select_option: "— Select —",
        license: "License/Permit",
        license_details: "License Details",
        license_details_placeholder: "Type of license and issuing organization",
        license_number: "Active License Number",
        license_number_placeholder: "Reference number or ID",
        jurisdictions_covered: "Jurisdictions Covered (where you can legally practice)",
        jurisdictions_hint: "Jurisdictions are not mandatory as not all can be listed",
        all_canada: "All Canada",
        all_usa: "All USA States",
        other_jurisdictions: "Other Jurisdictions (specify)",
        other_jurisdictions_placeholder: "E.g.: Mexico, Australia, etc.",
        add_license: "Add a license/permit ({{remaining}} remaining)",
      },
      step_three: {
        title: "3. Services & Compensation",
        errors_summary: "⚠️ Please correct the following errors before continuing:",
        services: "Detailed Description of Your Services",
        services_placeholder: "Describe how you help buyers/sellers/lenders (E.g.: Conducting IT/operational due diligence, negotiating LBO structure, etc.)",
        compensation_title: "Compensation Options",
        hourly_rate: "Hourly Rate",
        hourly_rate_placeholder: "E.g.: $150 - $250 / hour",
        hourly_rate_currency: "Hourly Rate Currency",
        project_rate: "Per Project Fee Details (Scope & Range)",
        project_rate_placeholder: "E.g.: $5,000 to $15,000 for a standard due diligence.",
        equity_partnership: "Willingness for Equity Partnership",
        equity_partnership_hint: "The exact equity terms will be negotiated directly with the Buyer.",
        currencies: "Accepted Currencies (multiple selection)",
        currencies_hint: "Check all that apply",
        availability: "Availability / Capacity",
        availability_placeholder: "— Select your availability —",
      },
      step_four: {
        title: "4. Compliance and Finalization",
        terms_title: "General User Agreement (Multi-Role, Version 2025-09-17 Rev. E)",
        terms_subtitle: "Please read the complete agreement, paying particular attention to Section 8 (Background Check) and Annex E (Consultants).",
        terms_accept: "I have read and accept the complete Terms and Conditions of Use, including provisions relating to my role as Consultant and compliance requirements.",
        background_check_title: "2. Formal Authorization for Background Check",
        background_check_subtitle: "This verification (KYC/AML) is a mandatory requirement for all professionals on the platform.",
        background_check_accept: "I formally authorize AcquizGroup and its designated partners to conduct a complete background check, as described in Section 8.",
        notice_title: "📌 Important Notice",
        notice_content: "After submission, we will proceed with the verification of your background and qualifications. A confirmation email will be sent to you shortly with the next steps.",
      },
      success: {
        title: "Registration Complete!",
        message: "Your questionnaire has been successfully submitted.",
        verification: "We will now proceed with the verification of your background and qualifications.",
        email: "A confirmation email will be sent to you shortly with the next steps.",
        redirecting: "Redirecting to dashboard...",
      },
      options: {
        yes: "Yes",
        no: "No",
        languages: {
          French: "French",
          English: "English",
          Spanish: "Spanish",
          German: "German",
          Italian: "Italian",
          Portuguese: "Portuguese",
          Russian: "Russian",
          Mandarin: "Mandarin",
          Arabic: "Arabic",
          Japanese: "Japanese",
          Other: "Other",
        },
        institution_categories: {
          independent_roles: "Independent Roles",
          business_services: "Business Services",
          specific_industries: "Specific Industries",
        },
        institution_types: {
          individual_self_employed: "Individual / Self-Employed",
          virtual_assistant: "Virtual Assistant / Call Center",
          researcher_consultant: "Researcher / Academic Consultant",
          coach_planner: "Coach / Business Planner",
          financial_firm: "Financial Firm (M&A Advisory, Brokerage, Wealth Management)",
          accounting_tax_firm: "Accounting/Tax Firm (CPA, CA, Tax Advisor)",
          law_firm: "Law Firm (Business Law, Corporate, IP)",
          marketing_pr_agency: "Marketing/PR Agency (SEO, Digital Strategy, Media)",
          it_software_firm: "IT/Software Firm (MSP, SaaS Consulting, Web Design)",
          human_resources: "Human Resources (Recruitment, Compensation, Talent)",
          media_agency: "Media Agency / Audio-Visual",
          business_intelligence: "Business Intelligence / Data Consulting",
          property_management: "Property Management",
          construction_trades: "Construction and Trades (General, Roofing, HVAC, Plumbing, Electrical)",
          residential_services: "Residential Services (Cleaning, Landscaping, Pool, Handyman)",
          manufacturing_operations: "Manufacturing and Industrial Operations",
          transportation_delivery: "Transportation, Trucking and Delivery Services",
          waste_management: "Waste Management and Water Treatment",
          agriculture_consulting: "Agriculture and Food Industry Consulting",
          retail_ecommerce: "Retail / E-commerce (Physical Store, Online Platform)",
          hospitality_tourism: "Hospitality / Tourism and Travel (Booking, Events)",
          healthcare_services: "Healthcare / Medical Services",
          automotive_industry: "Automotive Industry",
          aviation_marine: "Aviation / Marine Industry",
          sports_management: "Sports Retail / Sports and Events Management",
          interior_design: "Interior Design / Graphic Design Firm",
          other_specialized: "Other Specialized Consulting Firm",
        },
        industries: {
          agriculture_food: "AGRICULTURE, AGRI-FOOD & FOOD INDUSTRY",
          automotive_marine: "AUTOMOTIVE & BOAT / MARINE",
          beauty_personal: "BEAUTY & PERSONAL CARE",
          retail: "RETAIL (General, Sports, Specialized)",
          communication_media: "COMMUNICATION, MEDIA & PUBLIC RELATIONS",
          engineering_consulting: "ENGINEERING & TECHNICAL CONSULTING (Civil, Electrical, Chemical, Aero)",
          construction: "BUILDING & CONSTRUCTION (incl. Trades & HVAC)",
          entertainment_sport: "ENTERTAINMENT, RECREATION & SPORT",
          education: "EDUCATION & CHILDREN SERVICES",
          tech_saas: "ONLINE & TECHNOLOGY (SaaS, Web Dev, IT Services)",
          service_general: "SERVICE BUSINESSES (General)",
          manufacturing: "MANUFACTURING & INDUSTRIAL PRODUCTION",
          accounting_tax: "ACCOUNTING & TAX FIRM (CPA, CA)",
          legal_firm: "LEGAL FIRM (Corporate, IP, Real Estate)",
          waste_environmental: "WASTE MANAGEMENT, WATER TREATMENT & ENVIRONMENTAL",
          wholesale: "WHOLESALE & DISTRIBUTORS",
          business_intelligence: "BUSINESS INTELLIGENCE & DATA CONSULTING",
          non_classifiable: "NON-CLASSIFIABLE / OTHER",
          recruitment_hr: "RECRUITMENT & HUMAN RESOURCES",
          restaurant_food: "RESTAURANTS & FOOD SERVICE",
          home_services: "HOME SERVICES (Residential & Commercial Contractors)",
          financial_insurance: "FINANCIAL SERVICES & INSURANCE",
          real_estate: "REAL ESTATE SERVICES & PROPERTY MANAGEMENT",
          pet_services: "PET SERVICES",
          healthcare: "HEALTH CARE, MEDICAL & LIFE SCIENCES",
          transportation: "TRANSPORTATION, TRUCKING & LOGISTICS",
          travel_tourism: "TRAVEL, TOURISM & HOSPITALITY",
        },
        expertises: {
          business_owner: "Business owner in this sector",
          worker_in_sector: "Worker in this sector",
          licenses_permits: "Licenses, permits & certification in this sector",
          due_diligence_financial: "Due Diligence (Financial)",
          due_diligence_technical: "Due Diligence (Technical/Tech)",
          due_diligence_legal: "Due Diligence (Legal)",
          due_diligence_operational: "Due Diligence (Operational)",
          business_valuation: "Business Valuation",
          transaction_negotiation: "Transaction Negotiation and Structuring",
          acquisition_tax_planning: "Acquisition Tax Planning",
          financial_modeling: "Advanced Financial Modeling",
          working_capital_optimization: "Working Capital Optimization",
          banking_compliance: "Banking Regulation and Compliance",
          wealth_management: "Wealth Management",
          credit_risk_analysis: "Credit and Risk Analysis",
          brokerage_investments: "Brokerage and Investments",
          corporate_law: "Corporate Law and Governance",
          real_estate_law: "Real Estate Law (Zoning, Titles, Commercial Leases)",
          ma_law: "Mergers & Acquisitions Law (M&A Law)",
          labor_law: "Labor Law and Collective Agreements",
          business_litigation: "Business Litigation",
          ip_law: "Intellectual Property Law (IP)",
          tax_law: "Tax Law and Structuring",
          immigration_law: "Business Immigration Law",
          audit_certification: "Audit and Certification (CPA, CA)",
          tax_planning: "Tax Planning and Optimization",
          tax_compliance: "Tax Compliance",
          compliance_audit: "Regulatory Compliance Audit (AML, KYC)",
          management_accounting: "Management Accounting",
          internal_control: "Internal Control and Risk Management",
          civil_engineering: "Civil Engineering & Infrastructure",
          architecture: "Architecture and Urban Planning",
          mechanical_engineering: "Mechanical Engineering & Materials",
          industrial_engineering: "Industrial Engineering and Optimization (Lean/Six Sigma)",
          energy_engineering: "Energy Engineering",
          chemical_engineering: "Chemical Engineering & Processes",
          electrical_engineering: "Electrical & Electronic Engineering",
          aerospace_engineering: "Aerospace & Marine Engineering",
          interior_design: "Interior Design and Commercial Fit-Out",
          building_code_compliance: "Building Code Compliance and Safety Standards",
          construction_project_management: "Infrastructure and Construction Project Management",
          workforce_management: "Workforce Management / Specialized HR (Trades)",
          quality_control: "Quality Control (HVAC, Plumbing, Electrical, Roofing)",
          estimation_bidding: "Estimation and Bidding",
          site_coordination: "Site Coordination",
          cybersecurity_compliance: "Cyber-Security and Compliance (ISO, SOC 2, GDPR)",
          software_engineering: "Software Engineering and Web Development (DevOps, Full Stack)",
          software_architecture: "Software Architecture (Scalability, Cloud)",
          software_patents: "Software Patents and Tech IP Protection",
          business_intelligence: "Business Intelligence and Data Analysis",
          it_automation: "IT Process Automation (RPA)",
          infrastructure_networks: "Infrastructure and Networks",
          technical_support: "Technical Support and MSP",
          biomedical_engineering: "Biomedical Engineering",
          clinical_research: "Clinical and Scientific Research",
          biological_data_analysis: "Biological Data Analysis (Bioinformatics)",
          health_compliance: "Health Regulatory Compliance (FDA, Health Canada, HIPAA)",
          pharmacovigilance: "Pharmacovigilance and Medical Quality Assurance",
          equipment_operations: "Equipment Operations and Maintenance (Pharma)",
          packaging_line_management: "Pharmaceutical Packaging Line Management",
          medical_marketing: "Medical Marketing (Hospital Representative)",
          practice_management: "Medical Practice Management",
          nursing_care: "Nursing and Paramedical Care",
          post_merger_integration: "Post-Merger Integration (PMI)",
          lean_six_sigma: "Lean Manufacturing / Six Sigma",
          supply_chain_management: "Supply Chain Management",
          international_transportation: "International Transportation and Customs",
          specialized_logistics: "Specialized Logistics (Trucking, Marine, Aviation)",
          industrial_automation: "Industrial Automation (Industry 4.0)",
          inventory_management: "Stock and Inventory Management",
          production_optimization: "Production Operations Optimization",
          warehouse_management: "Warehouse Management",
          waste_water_management: "Waste Management and Water Treatment",
          environmental_compliance: "Environmental Compliance",
          impact_assessment: "Environmental Impact Assessment",
          sustainability_consulting: "Sustainability Consulting",
          site_remediation: "Contaminated Site Remediation",
          customer_acquisition: "Customer Acquisition Strategy (SEO/SEM/Social)",
          marketing_roi_analysis: "Marketing ROI Analysis and Attribution",
          public_relations: "Public Relations and Crisis Management",
          brand_strategy: "Brand Strategy and Positioning",
          advertising_campaigns: "Advertising and Media Campaigns",
          content_marketing: "Content Marketing and Copywriting",
          audiovisual_production: "Audio-Visual Production",
          media_distribution: "Media Distribution and Broadcasting",
          social_media_management: "Social Media Management",
          graphic_design: "Graphic Design and Visual Identity",
          advertising_design: "Advertising and Creative Design",
          sales_digital_transformation: "Sales Digital Transformation (Sales Tech)",
          business_development: "Business Development (B2B/B2C)",
          key_account_management: "Key Account Management",
          ecommerce_platforms: "E-commerce and Online Platforms",
          omnichannel_strategies: "Omnichannel Strategies",
          conversion_optimization: "Conversion Rate Optimization (CRO)",
          agricultural_consulting: "Agricultural/Agri-Food Consulting and Training",
          biotech_rd: "R&D in Biotechnology/Agri-Food",
          agricultural_development: "Agricultural and Forestry Development",
          food_regulation: "Food Regulation (HACCP, CFIA)",
          farm_management: "Farm Management",
          food_processing: "Food Processing",
          recruitment_staffing: "Recruitment and Staffing",
          compensation_benefits: "Compensation and Benefits Management",
          training_development: "Training and Development",
          talent_management: "Talent Management and Succession",
          labor_relations: "Labor Relations",
          occupational_health: "Occupational Health and Safety (OHS)",
          real_estate_appraisal: "Real Estate Appraisal",
          property_management: "Property Management",
          commercial_brokerage: "Commercial Real Estate Brokerage",
          real_estate_development: "Real Estate Development",
          leasing_administration: "Leasing and Administration",
          curriculum_development: "Curriculum Development",
          professional_education: "Professional and Continuing Education",
          specialized_education: "Specialized Educational Services",
          school_administration: "School Administration",
          tutoring_support: "Tutoring and Academic Support",
          automotive_repair: "Automotive Mechanics and Repair",
          bodywork_painting: "Body Work and Painting",
          vehicle_sales: "Vehicle Sales and Rental",
          marine_maintenance: "Marine Maintenance and Repair",
          marina_services: "Marina Services",
          hairdressing_aesthetics: "Hairdressing and Aesthetics",
          spa_wellness: "Spa and Wellness",
          salon_management: "Salon/Center Management",
          beauty_training: "Beauty Training",
          beauty_products_sales: "Beauty Products Sales",
          veterinary_care: "Veterinary Care",
          grooming: "Grooming",
          daycare_boarding: "Daycare and Boarding",
          training_behavior: "Training and Behavior",
          mobile_pet_services: "Mobile Pet Services",
          restaurant_management: "Restaurant Management",
          chef_cooking: "Chef and Cooking",
          service_experience: "Service and Customer Experience",
          procurement_control: "Procurement and Cost Control",
          menu_development: "Menu Concept and Development",
          store_management: "Store Management",
          visual_merchandising: "Visual Merchandising",
          pos_management: "Point of Sale Management (POS)",
          procurement_purchasing: "Procurement and Purchasing",
          customer_service: "Customer Service",
          hotel_management: "Hotel Management",
          concierge_services: "Concierge Services",
          event_planning: "Event Planning",
          travel_agency: "Travel Agency",
          tourist_attractions: "Tourist Attractions and Activities",
          sports_event_management: "Sports Event Management",
          coaching_training: "Coaching and Training",
          facility_management: "Recreational Facility Management",
          event_production: "Event Production",
          entertainment_shows: "Entertainment and Shows",
          plumbing_heating: "Plumber (Contractor) & Heating Systems",
          electrical_systems: "Electrician (Contractor) & Electrical Systems",
          hvac_ventilation: "HVAC (Contractor) & Ventilation/Air Conditioning Systems",
          roofing_siding: "Roofer (Roofing Contractor) & Siding Replacement",
          window_installation: "Window Installation & Repair (Contractor)",
          window_cleaning: "Window Cleaning (Contractor)",
          landscaping_pool: "Landscaping, Pool and Outdoor Maintenance",
          painting_finishes: "Painter (Contractor) & Interior/Exterior Finishes",
          handyman_repairs: "Handyman Services & Small Repairs",
          cleaning_maintenance: "Cleaning and Maintenance Business Management (Residential/Commercial)",
          distribution_management: "Distribution Management",
          supplier_relations: "Supplier Relations",
          distribution_logistics: "Distribution Logistics",
          b2b_wholesale: "B2B Wholesale Sales",
          translation: "Translation",
          interpretation: "Interpretation",
          writing: "Writing",
          virtual_assistant: "Virtual Assistant",
          web_research: "Web Research",
          database_creation: "Web Research and Database Creation",
        },
        experience_years: {
          "0-2": "0 to 2 years",
          "3-5": "3 to 5 years",
          "6-10": "6 to 10 years",
          "10+": "10 years and more",
        },
        jurisdictions: {
          canada: "Canada",
          usa: "United States",
          europe: "Europe (Selective Zone)",
        },
        compensation: {
          per_hour: "Hourly rate (specify below)",
          per_project: "Per project fee (specify below)",
          equity_partnership: "Partnership for equity stake in company to be acquired",
        },
        availability: {
          less_10h: "Less than 10 hours/week",
          "10_20h": "10 to 20 hours/week",
          more_20h: "More than 20 hours/week (part-time)",
          full_time: "Full-time (exclusive or near-exclusive)",
          to_be_agreed: "To be agreed / on demand (depending on transaction)",
        },
      },
    },
  },
  fr: {
    nav: {
      services: "Services",
      sellers: "Pour vendeurs",
      buyers: "Pour acheteurs",
      consultants: "Pour consultants",
      brokers: "Pour courtiers",
      internal_partners: "Partenaires internes",
      lenders: "Prêteurs",
      business_partners: "Partenaires d'affaires",
      opportunities_hub: "Hub des opportunités",
      resources: "Ressources",
      for_sale: "À vendre",
      buyer_plan: "Forfaits Acheteur",
      seller_plan: "Plans Vendeur & Courtier",
      deal_monitor: "Deal Monitor",
      blog: "Blog",
      elearning: "E-learning",
      faq: "FAQ",
      about: "À propos",
      our_experts: "Nos experts",
      business_immigration: "Immigration d'affaires",
      notary: "Notaire",
      tax_advisor: "Conseiller fiscal",
    },
    cta: {
      join_ecosystem_short: "Rejoindre l'écosystème",
      join_ecosystem_title: "Faites partie de l'écosystème d'AcquizGroup",
      join_ecosystem_desc: "Devenez un membre de notre réseau de prêteurs et partenaires d'affaires.",
      i_am_lender: "Je suis Prêteur",
      i_am_internal_partner: "Je suis Partenaire d'affaires",
    },
    index: {
      hero: {
        title: "Connecter l'ambition à l'action",
        subtitle: "La première plateforme intégrée pour acquérir, financer et transférer des entreprises à fort potentiel, au sein d'un écosystème sécurisé.",
        im_seller: "Je suis Vendeur",
        im_buyer: "Je suis Acheteur",
        im_consultant: "Je suis Consultant",
        im_broker: "Je suis Courtier",
        im_lender: "Je suis Prêteur",
        im_internal_expert: "Je suis Expert Interne",
      },
      financing: {
        title: "Redéfinir le financement d'acquisition",
        subtitle: "Parce que le succès d'un transfert d'entreprise repose sur des données irréprochables, nous avons bâti un standard de financement qui place la transparence et la performance au cœur de chaque transaction.",
        box1_title: "Élargir l'accès, réduire les biais",
        box1_desc: "Nous démocratisons le financement en éliminant les barrières traditionnelles et les biais d'allocation qui freinent les transactions prometteuses. Notre priorité : rapidité et équité.",
        box2_title: "La clarté par les chiffres",
        box2_desc: "La viabilité d'un projet ne devrait pas être un mystère. Nous basons nos analyses sur des fondamentaux indéniables, comme la marge bénéficiaire et un DSCR > 1,3, garantissant la qualité de chaque candidature.",
        box3_title: "La performance sans prospection",
        box3_desc: "Véritable moteur d'opportunités, nous connectons les prêteurs à un flux de clients hautement qualifiés. Nous optimisons l'écosystème sans jamais interférer dans les décisions de crédit finales.",
        learn_more: "En savoir plus",
      },
      ecosystem: {
        title: "L'écosystème AcquizGroup",
        subtitle: "Découvrez le bon chemin pour vous selon votre profil.",
        seller: "Vendeur",
        seller_desc: "Maximisez la valeur de votre entreprise grâce au processus de pré-financement, **suivez l'intérêt que suscite votre entreprise**, trouvez l'acheteur idéal et **recevez les demandes de contacts** via notre plateforme intelligente. Grâce à notre programme Consultant, découvrez les **experts et professionnels** dont vous avez besoin pour créer les meilleures conditions de vente.",
        sell_cta: "Vendre mon entreprise",
        buyer: "Acheteur",
        buyer_desc_1: "Ne manquez plus jamais une opportunité à fort potentiel.",
        buyer_desc_2: "Bénéficiez de notre réseau de consultants et créez des partenariats gagnants.",
        buyer_desc_3: "Structurez votre plan d'acquisition, du financement à l'intégration, via notre plateforme intelligente.",
        buy_cta: "Acheter une entreprise",
        consultant: "Consultant",
        consultant_desc_1: "Votre **Savoir-faire et vos Licences** valent de l'or.",
        consultant_desc_2: "Offrez vos services **contre rémunération ou une part de l'équity** aux propriétaires d'entreprises ou aux acheteurs.",
        consultant_desc_3: "Créez des partenariats en partageant votre expertise, vos compétences, vos licences et plus encore.",
        consultant_desc_4: "Devenez un élément clé du processus d'acquisition et de transfert d'entreprise.",
        consultant_cta: "Devenir Consultant",
        lender: "Prêteur",
        lender_desc:
          "Recevez un flux régulier d'opportunités qualifiées correspondant à vos critères de financement. Accédez à des dossiers complets et vérifiés pour des décisions de crédit plus rapides et mieux informées.",
        lender_cta: "Devenir Prêteur",
        broker: "Courtier",
        broker_desc:
          "Gérez les transactions de vos clients de bout en bout depuis un seul espace. Soumettez et suivez vos deals, **ajoutez les vendeurs que vous représentez**, recevez les demandes de contacts et les détails des profils acheteurs conjointement, **coordonnez votre calendrier avec celui de vos clients**, et **redonnez vie à vos deals dormants**. Simplifiez chaque étape du processus.",
        broker_cta: "Devenir Courtier",
        closing_expert: "Expert du Closing",
        closing_expert_desc:
          "**Que vous soyez avocat en droit des affaires ou en immigration entrepreneuriale, notaire, conseiller fiscal ou comptable**, offrez vos services directement sur le tableau de bord de chaque usager de la plateforme et/ou directement sur les annonces d'entreprises à vendre de votre zone de service.",
        closing_expert_cta: "Devenir Expert du Closing",
      },
      why: {
        title: "Pourquoi choisir AcquizGroup ?",
        subtitle: "Dans un marché où la transition d'entreprise est un défi majeur, nous vous offrons précision opérationnelle et écoute humaine pour faire de votre transaction un succès.",
        card1_title: "Approche humaine et personnalisée",
        card1_desc: "Nous savons que chaque transaction est unique. Chez AcquizGroup, nous croyons qu'une transaction réussie repose sur l'équilibre parfait entre efficacité technologique et chaleur humaine. AcquizGroup, c'est la puissance du numérique enrichie par un accompagnement professionnel dédié.",
        card2_title: "Innovation technologique",
        card2_desc: "AcquizGroup offre plus qu'une simple plateforme : nous fournissons un écosystème de confiance qui vous aide à atteindre vos objectifs en toute sérénité, en vous faisant gagner du temps et de l'argent. Grâce à cette innovation, fini les échanges éparpillés par courriel, Dropbox, Zoom, etc. Tout est intégré : partage de documents, calendrier, vidéoconférence, transcription, vérification et suivi.",
        card3_title: "Réseau de partenaires fiables",
        card3_desc: "Accédez à un vaste répertoire d'experts qualifiés dans chaque secteur d'activité. Parce que la sécurité de votre transaction est notre priorité absolue, chaque consultant de notre réseau fait l'objet d'un processus de vérification rigoureux pour garantir l'intégrité des services et prévenir la fraude.",
      },
      newsletter: {
        title: "Restez informé",
        subtitle: "Abonnez-vous à notre infolettre pour les dernières transactions, analyses et tendances du marché.",
        placeholder: "Entrez votre courriel",
        subscribe: "S'abonner",
        subscribing: "Abonnement...",
      },
      partner_cta: {
        title: "Rejoignez notre écosystème",
        subtitle: "Vous êtes prêteur ou partenaire d'affaires ? Rejoignez notre réseau de confiance et accédez à des opportunités exclusives.",
        im_lender: "Je suis Prêteur",
        im_partner: "Je suis Partenaire d'affaires",
      },
      footer: {
        about: "À propos",
        about_desc: "Nous facilitons les partenariats, la transmission d'entreprise et la structuration opérationnelle.",
        transactions: "Transactions",
        sell: "Vendre",
        buy: "Acheter",
        create_listing: "Créer un listing",
        partners: "Partenaires",
        consultants: "Consultants",
        partners_link: "Partenaires",
        lenders: "Prêteurs",
        resources: "Ressources",
        deal_monitor: "Deal Monitor",
        contact: "Contact",
        contact_desc: "Discutez de vos projets d'acquisition ou de transmission.",
        contact_team: "Contacter l'équipe",
        terms: "Termes et Conditions",
        privacy: "Politique de confidentialité",
        cookies: "Politique de cookies",
        copyright: "© 2026 AcquizGroup. Tous droits réservés.",
      },
      sign_in: "Se connecter",
      business_lawyers: "Avocats d'affaires",
      immigration_lawyers: "Avocats en immigration",
    },
    footer: {
      about_title: "À propos d'AcquizGroup",
      about_description: "Nous sommes des facilitateurs de partenariats, de transmission d'entreprise et de structuration opérationnelle. Notre mission est de lier l'ambition à l'action.",
      services: "Alliés clés",
      sell_business: "Vendre une entreprise",
      buy_business: "Acheter une entreprise",
      become_partner: "Nos experts",
      resources: "Ressources",
      contact: "Contact",
      contact_description: "Contactez-nous pour discuter de vos projets d'acquisition ou de transmission.",
      contact_us: "Contacter l'équipe",
      copyright: "© 2024 AcquizGroup. Tous droits réservés.",
      terms: "Termes et Conditions",
      privacy: "Politique de confidentialité",
      cookies: "Gérer les cookies",
      cookies_policy: "Politique des cookies",
    },
    listings: {
      tagline: "KEEP GROWING WITH US",
      sign_in: "Connexion",
      cash_flow: "Flux",
      pre_financing_offers: "offres de pré-financement",
      add_favorite: "Ajouter aux favoris",
      remove_favorite: "Retirer des favoris",
      added_to_favorites: "Ajouté aux favoris ❤️",
      removed_from_favorites: "Retiré des favoris",
    },
    admin: {
      nav: {
        dashboard: "Tableau de bord",
        users: "Utilisateurs",
        companies: "Entreprises",
        deals: "Transactions",
        submissions: "Soumissions",
        messages: "Messages & Chat",
        calls: "Appels & Réunions",
        documents: "Documents",
        partners: "Partenaires",
        operations: "Opérations",
        calendar: "Calendrier",
        finance: "Finance",
        kpis: "Indicateurs",
      },
      dashboard: {
        title: "Portail d'administration",
        create_deal: "Créer une transaction",
        view_all: "Voir tout",
        users_card: "Utilisateurs",
        deals_card: "Transactions",
        submissions_card: "Soumissions",
        no_users: "Aucun utilisateur",
        no_deals: "Aucune transaction",
        no_submissions: "Aucune soumission",
      },
    },
    broker_registration: {
      page_title: "Questionnaire d'Inscription Courtier",
      steps: {
        name: "Étape",
        of: "sur",
        profile: "Profil",
        expertise: "Expertise",
        model: "Modèle",
        methodology: "Méthodologie",
        compliance: "Conformité",
      },
      step_one: {
        title: "1. Profil du Courtier",
        first_name: "Prénom",
        first_name_placeholder: "Ex: Jean",
        last_name: "Nom de famille",
        last_name_placeholder: "Ex: Dupont",
        firm_name: "Nom de la Firme de Courtage",
        firm_name_placeholder: "Ex: Courtiers d'Affaires Dupont Inc.",
        email: "Courriel de Contact",
        email_placeholder: "Ex: jean@courtiers-dupont.com",
        phone: "Téléphone",
        phone_placeholder: "Ex: +1-514-555-1234",
        password: "Mot de passe",
        password_placeholder: "Entrez votre mot de passe",
        confirm_password: "Confirmer le mot de passe",
        confirm_password_placeholder: "Confirmez votre mot de passe",
        address_type: "Type d'adresse",
        address_type_residential: "Résidentielle",
        address_type_corporate: "Corporative",
        address_search: "Recherche d'adresse complète",
        address_search_placeholder: "Tapez votre adresse pour la rechercher",
        street_address: "Adresse complète",
        street_address_placeholder: "123 Rue Principale, Bureau 400",
        city: "Ville",
        city_placeholder: "Ex: Montréal",
        state_province: "Province/État",
        state_province_placeholder: "Ex: Québec",
        zip_postal_code: "Code postal",
        zip_postal_code_placeholder: "Ex: H3B 2C9",
        country: "Pays",
        mother_tongue: "Langue maternelle",
        spoken_languages: "Autres langues parlées",
        spoken_languages_hint: "(Cochez toutes celles qui s'appliquent)",
        profile_enhancement: "Amélioration du Profil (Optionnel)",
        profile_photo: "Photo de Profil",
        photo_hint: "Une photo professionnelle aide à établir la confiance avec les vendeurs et acheteurs. Max 5MB, JPG ou PNG.",
        upload_photo: "Télécharger une Photo",
        change_photo: "Changer la Photo",
        professional_description: "Description Professionnelle",
        description_hint: "Décrivez votre expérience et ce qui fait de vous un courtier de confiance (optionnel)",
        description_placeholder: "Partagez votre parcours professionnel, vos spécialisations, vos années d'expérience et pourquoi les clients devraient travailler avec vous...",
      },
      step_two: {
        title: "2. Expertise & Licenses",
        subtitle: "Domaines d'expertise et certifications professionnelles",
        specializations_title: "Domaines d'Expertise",
        add_specialization: "Ajouter un domaine",
        primary_sector: "Secteur principal",
        primary_sector_placeholder: "Sélectionnez un secteur",
        years_experience: "Années d'expérience",
        years_experience_placeholder: "Sélectionnez votre expérience",
        expertises: "Types d'expertise",
        expertises_hint: "(Cochez toutes celles qui s'appliquent)",
        licenses_title: "Licenses Professionnelles",
        add_license: "Ajouter une license",
        license_details: "Détails de la license",
        license_details_placeholder: "Ex: Certified Business Intermediary (CBI)",
        license_number: "Numéro de license",
        license_number_placeholder: "Ex: CBI-12345",
        jurisdictions: "Juridictions",
        jurisdictions_hint: "(Cochez toutes celles qui s'appliquent)",
        other_jurisdictions: "Autres juridictions",
        other_jurisdictions_placeholder: "Spécifiez les autres juridictions si applicable",
      },
      step_three: {
        title: "3. Modèle Transactionnel",
        subtitle: "Votre approche de transaction et accords",
        transaction_types: "Types de transactions que vous facilitez",
        transaction_types_hint: "(Cochez toutes celles qui s'appliquent)",
        nda_enforcement: "Documents produits par la plateforme pour conformité",
        nda_enforcement_hint: "En cochant ces cases vous donnez votre accord pour que :",
        nda_prefinancing_share: "AcquizGroup partage avec les prêteurs de la plateforme les informations relatives aux entreprises que vous soumettez, pour l'étape de préfinancement.",
        nda_standardized_documents: "Pour faciliter la cohésion sur toute l'ensemble de la plateforme, AcquizGroup utilisera ses propres documents NDA, CIM et LOI.",
        nda_positive_cashflow: "Seules les entreprises ayant un flux de trésorerie positif seront acceptées.",
        nda_evaluation: "AcquizGroup mènera sa propre évaluation des entreprises fournies.",
      },
      step_four: {
        title: "4. Méthodologie (KPIs)",
        subtitle: "Indicateurs de performance clés et critères",
        post_sale_role: "Rôle post-vente",
        post_sale_role_hint: "(Cochez toutes celles qui s'appliquent)",
        vetting_process: "Exigences de preuve de fonds (PoF)",
        vetting_process_hint: "(Cochez toutes celles qui s'appliquent)",
        cash_down_min_percent: "Pourcentage minimum de mise de fonds",
        fixed_amount_min: "Montant liquide minimum",
        fixed_amount_max: "Montant liquide maximum",
        accepted_pof_types: "Types de PoF acceptés",
        accepted_pof_types_hint: "(Cochez toutes celles qui s'appliquent)",
        communication_style: "Style de communication préféré",
        communication_style_hint: "(Cochez toutes celles qui s'appliquent)",
        competing_offers: "Gestion des offres concurrentes",
        competing_offers_hint: "(Cochez toutes celles qui s'appliquent)",
        deal_size_min: "Taille de transaction minimale",
        deal_size_max: "Taille de transaction maximale",
        fee_structure: "Structure de frais",
        fee_structure_hint: "(Cochez toutes celles qui s'appliquent)",
      },
      step_five: {
        title: "5. Conformité et Consentement",
        subtitle: "Termes et conditions, vérification d'antécédents",
        terms_title: "Termes et Conditions",
        terms_checkbox: "J'ai lu et j'accepte les termes et conditions ci-dessus",
        background_check_title: "Vérification d'Antécédents",
        background_check_content: "AcquizGroup effectue des vérifications d'antécédents sur tous les courtiers inscrits pour assurer la sécurité et la conformité de la plateforme.\n\nCette vérification peut inclure:\n• Vérification de l'identité\n• Vérification du casier judiciaire\n• Vérification des références professionnelles\n• Validation des licenses et certifications\n\nToutes les informations sont traitées de manière confidentielle et en conformité avec les lois applicables sur la protection des données.",
        background_check_checkbox: "J'autorise formellement AcquizGroup à effectuer une vérification d'antécédents et confirme que toutes les informations fournies sont exactes",
      },
      navigation: {
        previous: "← Précédent",
        next: "Suivant →",
        submit: "Soumettre le Questionnaire",
        submitting: "Soumission en cours...",
      },
      success: {
        title: "Inscription Soumise avec Succès!",
        message: "Merci d'avoir complété le formulaire d'inscription courtier.",
        next_steps: "Prochaines étapes:",
        step_1: "Vous recevrez un courriel de confirmation sous peu",
        step_2: "Une fois confirmé, vous aurez accès à votre tableau de bord",
        step_3: "",
        redirecting: "Redirection vers le tableau de bord dans",
        seconds: "secondes",
      },
      already_member: "Déjà membre ?",
      login: "Connexion",
      errors: {
        broker_name_required: "Le nom du courtier est requis",
        firm_name_required: "Le nom de la firme est requis",
        email_required: "L'adresse courriel est requise",
        email_invalid: "Format d'adresse courriel invalide",
        phone_required: "Le numéro de téléphone est requis",
        street_address_required: "L'adresse complète est requise",
        city_required: "La ville est requise",
        state_province_required: "La province/état est requis(e)",
        zip_postal_code_required: "Le code postal est requis",
        country_required: "Le pays est requis",
        mother_tongue_required: "La langue maternelle est requise",
        spoken_languages_required: "Au moins une autre langue doit être sélectionnée",
        specializations_required: "Au moins un domaine d'expertise est requis",
        industry_required: "Le secteur est requis",
        expertises_required: "Au moins un type d'expertise doit être sélectionné",
        experience_required: "Les années d'expérience sont requises",
        licenses_required: "Au moins une license est requise",
        license_details_required: "Les détails de la license sont requis",
        license_number_required: "Le numéro de license est requis",
        jurisdictions_required: "Au moins une juridiction doit être sélectionnée",
        transaction_types_required: "Au moins un type de transaction doit être sélectionné",
        evaluation_process_required: "Au moins un processus d'évaluation doit être sélectionné",
        nda_enforcement_required: "L'application des NDA est requise",
        post_sale_role_required: "Au moins un rôle post-vente doit être sélectionné",
        vetting_process_required: "Au moins une exigence de PoF doit être sélectionnée",
        cash_down_percent_required: "Le pourcentage de mise de fonds est requis lorsque l'option est cochée",
        fixed_amounts_required: "Les montants minimum et maximum sont requis lorsque l'option est cochée",
        accepted_pof_types_required: "Au moins un type de PoF doit être sélectionné",
        communication_style_required: "Au moins un style de communication doit être sélectionné",
        competing_offers_required: "La gestion des offres concurrentes est requise",
        deal_size_min_required: "La taille de transaction minimale est requise",
        deal_size_max_required: "La taille de transaction maximale est requise",
        fee_structure_required: "Au moins une structure de frais doit être sélectionnée",
        terms_required: "Vous devez accepter les termes et conditions",
        background_check_required: "Vous devez accepter la vérification d'antécédents",
      },
      options: {
        countries: {
          CA: "Canada",
          US: "États-Unis",
          FR: "France",
          UK: "Royaume-Uni",
          OTHER: "Autre",
        },
        address_types: {
          residential: "Résidentielle",
          corporate: "Corporative",
        },
        languages: {
          French: "Français",
          English: "Anglais",
          Spanish: "Espagnol",
          German: "Allemand",
          Italian: "Italien",
          Portuguese: "Portugais",
          Russian: "Russe",
          Mandarin: "Mandarin",
          Arabic: "Arabe",
          Japanese: "Japonais",
          Other: "Autre",
        },
        industries: {
          all: "Tout",
          agriculture_food: "Agriculture, Agroalimentaire & Industrie Alimentaire",
          automotive_marine: "Automobile & Bateau / Marine",
          beauty_personal: "Beauté & Soins Personnels",
          construction: "Bâtiment & Construction (incl. Métiers & CVCA)",
          communication_media: "Communication, Médias & Relations Publiques",
          education: "Éducation & Services pour Enfants",
          entertainment_sport: "Divertissement, Loisirs & Sport",
          financial_insurance: "Services Financiers & Assurances",
          healthcare: "Soins de Santé, Médical & Sciences de la Vie",
          manufacturing: "Fabrication & Production Industrielle",
          tech_saas: "En Ligne & Technologie (SaaS, Développement Web, Services TI)",
          pet_services: "Services pour Animaux",
          restaurant_food: "Restaurants & Services Alimentaires",
          retail: "Commerce de Détail (Général, Sport, Spécialisé)",
          service_general: "Entreprises de Services (Général)",
          transportation: "Transport, Camionnage & Logistique",
          travel_tourism: "Voyage, Tourisme & Hôtellerie",
          wholesale: "Grossistes & Distributeurs",
          accounting_tax: "Cabinet Comptable & Fiscal (CPA, CA)",
          legal_firm: "Cabinet Juridique (Corporatif, PI, Immobilier)",
          engineering_consulting: "Ingénierie & Conseil Technique (Civil, Électrique, Chimique, Aéro)",
          real_estate: "Services Immobiliers & Gestion Immobilière",
          waste_environmental: "Gestion des Déchets, Traitement de l'Eau & Environnemental",
          business_intelligence: "Intelligence d'Affaires & Conseil en Données",
          recruitment_hr: "Recrutement & Ressources Humaines",
        },
        expertises: {
          financial_analysis: "Analyse financière",
          valuation: "Évaluation d'entreprise",
          legal_due_diligence: "Vérification diligente juridique",
          market_research: "Étude de marché",
          negotiation: "Négociation",
        },
        experience_years: {
          "0-5": "0 à 5 ans",
          "6-10": "6 à 10 ans",
          "10+": "10+ ans",
        },
        transaction_types: {
          asset_purchase: "Achat d'Actifs",
          share_purchase: "Achat d'Actions",
          merger: "Fusion",
          partnership: "Partenariat / Coentreprise",
        },
        evaluation_process: {
          credit_check: "Vérification de Crédit",
          financial_review: "Examen Financier",
          industry_experience: "Examen de l'Expérience Sectorielle",
          interview: "Entrevue en Personne",
        },
        nda_enforcement: {
          mandatory_all: "Obligatoire pour tous les acheteurs avant divulgation",
          mandatory_financials: "Obligatoire avant divulgation financière seulement",
          case_by_case: "Au cas par cas",
          not_required: "Non requis systématiquement",
        },
        post_sale_role: {
          transition_support: "Support de Transition",
          ongoing_advisory: "Conseil Continu (6-12 mois)",
          none: "Aucun - Transaction seulement",
        },
        pof_requirements: {
          cash_down_payment: "Mise de Fonds en Espèces (% du prix d'achat)",
          fixed_liquid_amount: "Montant Liquide Fixe (indépendant du prix)",
          pre_approval_letter: "Lettre de Pré-Approbation du Prêteur",
          no_pof_required: "Aucune PoF requise d'avance",
        },
        cash_down_percent: {
          "10": "10%",
          "20": "20%",
          "30": "30%",
          "40": "40%+",
        },
        fixed_amounts: {
          "50k": "50k $",
          "100k": "100k $",
          "250k": "250k $",
          "500k": "500k $+",
          "1M+": "1M $+",
        },
        pof_types: {
          bank_statement: "Relevé Bancaire (3 mois)",
          bank_letter: "Lettre de Confort Bancaire",
          investment_statement: "Relevé de Compte de Placement",
          lender_preapproval: "Pré-Approbation du Prêteur",
        },
        communication_style: {
          phone: "Appels Téléphoniques",
          email: "Courriel",
          video: "Vidéoconférence",
          in_person: "Réunions en Personne",
        },
        competing_offers: {
          transparent_disclosure: "Divulgation transparente à toutes les parties",
          blind_bidding: "Processus d'enchères à l'aveugle",
          best_and_final: "Ronde d'offre meilleure et finale",
          first_come: "Premier arrivé, premier servi",
        },
        deal_sizes: {
          under_500k: "Moins de 500k $",
          "500k_1M": "500k $ - 1M $",
          "1M_2M": "1M $ - 2M $",
          "2M_5M": "2M $ - 5M $",
          "5M_10M": "5M $ - 10M $",
          "10M_25M": "10M $ - 25M $",
          "25M_50M": "25M $ - 50M $",
          "50M_plus": "50M $+",
        },
        fee_structure: {
          success_only: "Frais de Succès Seulement",
          retainer_plus_success: "Honoraires + Frais de Succès",
          hourly: "Tarif Horaire",
          flat_fee: "Frais Fixes",
        },
      },
    },
    consultant_registration: {
      page_title: "Questionnaire d'Inscription Consultant",
      steps: {
        name: "Étape",
        of: "de",
        account: "Compte",
        profile: "Profil",
        services: "Services",
        compliance: "Conformité",
      },
      step_one: {
        title: "1. Informations de Compte & Adresse Résidentielle",
        personal_info: "Informations Personnelles",
        full_name: "Nom complet",
        full_name_placeholder: "Ex: Marie Dubois",
        email: "Email",
        email_placeholder: "vous@firme.com",
        phone: "Téléphone",
        phone_placeholder: "+1 514-...",
        institution_types: "Type(s) d'Institution / Rôle Principal",
        institution_types_hint: "Sélectionnez un ou plusieurs types (sélection multiple autorisée)",
        languages: "Langues",
        mother_tongue: "Langue maternelle",
        mother_tongue_placeholder: "— Sélectionnez votre langue maternelle —",
        spoken_languages: "Autres langues parlées (optionnel)",
        spoken_languages_hint: "Sélectionnez toutes les langues que vous parlez couramment",
        address_title: "Adresse Résidentielle Actuelle",
        address_subtitle: "Requise pour la vérification des antécédents (Background Check)",
        address_search: "Recherche d'adresse",
        address_search_placeholder: "Commencez à taper votre adresse...",
        address_search_hint: "Tapez votre adresse et sélectionnez-la dans la liste déroulante",
        street_address: "Adresse complète (Rue, Apt/Unité)",
        street_address_placeholder: "123 Main St, Apt 4",
        street_address_hint: "Rempli automatiquement par la recherche, mais modifiable manuellement",
        city: "Ville",
        city_placeholder: "Miami",
        state_province: "État/Province",
        state_province_placeholder: "FL",
        zip_postal_code: "Code Postal",
        zip_postal_code_placeholder: "33101",
        country: "Pays",
        country_placeholder: "USA",
        account_security: "Sécurité du Compte",
        password: "Mot de passe",
        password_placeholder: "Min. 6 caractères",
        confirm_password: "Confirmer le mot de passe",
        confirm_password_placeholder: "Re-saisissez votre mot de passe",
      },
      step_two: {
        title: "2. Profil & Expertise",
        errors_summary: "⚠️ Veuillez corriger les erreurs suivantes avant de continuer:",
        company_name: "Nom de l'entreprise (optionnel)",
        company_name_placeholder: "Votre firme (si applicable)",
        sectors_title: "Secteurs d'Activité & Expertises",
        sectors_subtitle: "Sélectionnez jusqu'à 3 secteurs d'activité pour lesquels vous aimeriez promouvoir vos services ou dans lesquels vous aimeriez créer un partenariat",
        sector: "Secteur",
        remove: "Retirer",
        business_sector: "Secteur d'Activité",
        select_sector: "— Sélectionnez un secteur —",
        key_expertises: "Expertises Connexes Clés (sélection multiple)",
        key_expertises_hint: "Sélectionnez au moins une expertise",
        years_experience: "Années d'expérience dans ce secteur",
        select_experience: "— Sélectionnez —",
        add_sector: "Ajouter un secteur d'activité ({{remaining}} restant)",
        licenses_title: "Licences & Permis",
        has_license: "Détenez-vous des licences professionnelles actives?",
        select_option: "— Sélectionner —",
        license: "Licence/Permis",
        license_details: "Détails de la licence/permis",
        license_details_placeholder: "Type de licence et organisme émetteur",
        license_number: "Numéro de licence actif",
        license_number_placeholder: "Numéro de référence ou ID",
        jurisdictions_covered: "Juridictions Couvertes (où vous pouvez légalement exercer)",
        jurisdictions_hint: "Les juridictions ne sont pas obligatoires car toutes ne peuvent être listées",
        all_canada: "Tout le Canada",
        all_usa: "Tous les États",
        other_jurisdictions: "Autres Juridictions (à spécifier)",
        other_jurisdictions_placeholder: "Ex: Mexique, Australie, etc.",
        add_license: "Ajouter une licence/permis ({{remaining}} restante)",
      },
      step_three: {
        title: "3. Services & Compensation",
        errors_summary: "⚠️ Veuillez corriger les erreurs suivantes avant de continuer:",
        services: "Description détaillée de vos services",
        services_placeholder: "Décrivez comment vous aidez les acheteurs/vendeurs/prêteurs (Ex: Conduite de due diligence IT/opérationnelle, négociation de structure LBO, etc.)",
        compensation_title: "Options de Compensation",
        hourly_rate: "Taux horaire",
        hourly_rate_placeholder: "Ex: 150$ - 250$ / heure",
        hourly_rate_currency: "Devise du taux horaire",
        project_rate: "Détails du forfait par projet (Portée & Fourchette)",
        project_rate_placeholder: "Ex: 5 000$ à 15 000$ pour une due diligence standard.",
        equity_partnership: "Volonté de partenariat en équité",
        equity_partnership_hint: "Les termes exacts de l'équité seront négociés directement avec l'Acheteur.",
        currencies: "Devises acceptées (sélection multiple)",
        currencies_hint: "Cochez toutes celles qui s'appliquent",
        availability: "Disponibilité / Capacité",
        availability_placeholder: "— Sélectionnez votre disponibilité —",
      },
      step_four: {
        title: "4. Conformité et Finalisation",
        terms_title: "Accord Général d'Utilisation (Multi-Rôle, Version 2025-09-17 Rev. E)",
        terms_subtitle: "Veuillez lire l'accord complet, en portant une attention particulière à la Section 8 (Vérification des Antécédents) et à l'Annexe E (Consultants).",
        terms_accept: "J'ai lu et j'accepte l'intégralité des Termes et Conditions d'Utilisation, incluant les dispositions relatives à mon rôle de Consultant et les exigences de conformité.",
        background_check_title: "2. Autorisation Formelle de la Vérification des Antécédents",
        background_check_subtitle: "Cette vérification (KYC/AML) est une exigence obligatoire pour tous les professionnels sur la plateforme.",
        background_check_accept: "J'autorise formellement AcquizGroup et ses partenaires désignés à procéder à une vérification complète des antécédents, tel que décrit dans la Section 8.",
        notice_title: "📌 Avis Important",
        notice_content: "Après soumission, nous procéderons à la vérification de vos antécédents et qualifications. Un courriel de confirmation vous sera envoyé sous peu avec les prochaines étapes.",
      },
      success: {
        title: "Inscription terminée!",
        message: "Votre questionnaire a été soumis avec succès.",
        verification: "Nous allons maintenant procéder à la vérification de vos antécédents et qualifications.",
        email: "Un courriel de confirmation vous sera envoyé sous peu avec les prochaines étapes.",
        redirecting: "Redirection vers le tableau de bord...",
      },
      options: {
        yes: "Oui",
        no: "Non",
        languages: {
          French: "Français",
          English: "Anglais",
          Spanish: "Espagnol",
          German: "Allemand",
          Italian: "Italien",
          Portuguese: "Portugais",
          Russian: "Russe",
          Mandarin: "Mandarin",
          Arabic: "Arabe",
          Japanese: "Japonais",
          Other: "Autre",
        },
        institution_categories: {
          independent_roles: "Rôles Indépendants",
          business_services: "Services aux Entreprises",
          specific_industries: "Industries Spécifiques",
        },
        institution_types: {
          individual_self_employed: "Individuel / Travailleur Autonome",
          virtual_assistant: "Assistant Virtuel / Centre d'Appels",
          researcher_consultant: "Chercheur / Consultant Académique",
          coach_planner: "Coach / Planificateur d'Affaires",
          financial_firm: "Firme Financière (Conseil M&A, Courtage, Gestion de Patrimoine)",
          accounting_tax_firm: "Firme Comptable/Fiscale (CPA, CA, Conseiller Fiscal)",
          law_firm: "Firme Juridique (Droit des Affaires, Corporatif, PI)",
          marketing_pr_agency: "Agence Marketing/RP (SEO, Stratégie Numérique, Médias)",
          it_software_firm: "Firme TI/Logiciels (MSP, Conseil SaaS, Conception Web)",
          human_resources: "Ressources Humaines (Recrutement, Rémunération, Talents)",
          media_agency: "Agence Média / Audio-Visuel",
          business_intelligence: "Intelligence d'Affaires / Conseil en Données",
          property_management: "Gestion Immobilière",
          construction_trades: "Construction et Métiers (Général, Toiture, CVC, Plomberie, Électricité)",
          residential_services: "Services Résidentiels (Nettoyage, Aménagement Paysager, Piscine, Bricolage)",
          manufacturing_operations: "Fabrication et Opérations Industrielles",
          transportation_delivery: "Transport, Camionnage et Services de Livraison",
          waste_management: "Gestion des Déchets et Traitement de l'Eau",
          agriculture_consulting: "Agriculture et Conseil Industrie Alimentaire",
          retail_ecommerce: "Commerce de Détail / E-commerce (Magasin Physique, Plateforme en Ligne)",
          hospitality_tourism: "Hôtellerie / Tourisme et Voyages (Réservation, Événements)",
          healthcare_services: "Soins de Santé / Services Médicaux",
          automotive_industry: "Industrie Automobile",
          aviation_marine: "Industrie Aviation / Nautique",
          sports_management: "Commerce Sport / Gestion Sport et Événements",
          interior_design: "Design d'Intérieur / Firme de Design Graphique",
          other_specialized: "Autre Firme de Conseil Spécialisée",
        },
        industries: {
          agriculture_food: "AGRICULTURE, AGRO-ALIMENTAIRE & INDUSTRIE ALIMENTAIRE",
          automotive_marine: "AUTOMOBILE & BATEAU / MARINE",
          beauty_personal: "BEAUTÉ & SOINS PERSONNELS",
          retail: "COMMERCE DE DÉTAIL (Général, Sport, Spécialisé)",
          communication_media: "COMMUNICATION, MÉDIAS & RELATIONS PUBLIQUES",
          engineering_consulting: "CONSEIL EN GÉNIE & TECHNIQUE (Civil, Électrique, Chimique, Aéro)",
          construction: "CONSTRUCTION & BÂTIMENT (incl. Métiers & CVC)",
          entertainment_sport: "DIVERTISSEMENT, LOISIRS & SPORT",
          education: "ÉDUCATION & SERVICES AUX ENFANTS",
          tech_saas: "EN LIGNE & TECHNOLOGIE (SaaS, Dév. Web, Services TI)",
          service_general: "ENTREPRISES DE SERVICES (Général)",
          manufacturing: "FABRICATION & PRODUCTION INDUSTRIELLE",
          accounting_tax: "FIRME COMPTABLE & FISCALE (CPA, CA)",
          legal_firm: "FIRME JURIDIQUE (Corporatif, PI, Immobilier)",
          waste_environmental: "GESTION DES DÉCHETS, TRAITEMENT DE L'EAU & ENVIRONNEMENT",
          wholesale: "GROSSISTES & DISTRIBUTEURS",
          business_intelligence: "INTELLIGENCE D'AFFAIRES & CONSEIL EN DONNÉES",
          non_classifiable: "NON CLASSIFIABLE / AUTRE",
          recruitment_hr: "RECRUTEMENT & RESSOURCES HUMAINES",
          restaurant_food: "RESTAURANTS & SERVICES ALIMENTAIRES",
          home_services: "SERVICES À DOMICILE (Contracteurs Résidentiels & Commerciaux)",
          financial_insurance: "SERVICES FINANCIERS & ASSURANCE",
          real_estate: "SERVICES IMMOBILIERS & GESTION DE PROPRIÉTÉS",
          pet_services: "SERVICES POUR ANIMAUX",
          healthcare: "SOINS DE SANTÉ, MÉDICAL & SCIENCES DE LA VIE",
          transportation: "TRANSPORT, CAMIONNAGE & LOGISTIQUE",
          travel_tourism: "VOYAGE, TOURISME & HÔTELLERIE",
        },
        expertises: {
          business_owner: "Propriétaire d'une entreprise dans ce secteur",
          worker_in_sector: "Travailleur dans ce secteur",
          licenses_permits: "Licences, permis & carte de compétence dans ce secteur",
          due_diligence_financial: "Due Diligence (Financière)",
          due_diligence_technical: "Due Diligence (Technique/Tech)",
          due_diligence_legal: "Due Diligence (Juridique/Légale)",
          due_diligence_operational: "Due Diligence (Opérationnelle)",
          business_valuation: "Évaluation d'Entreprise (Valuation)",
          transaction_negotiation: "Négociation et Structuration de Transactions",
          acquisition_tax_planning: "Planification Fiscale d'Acquisition",
          financial_modeling: "Modélisation Financière Avancée",
          working_capital_optimization: "Optimisation du Fonds de Roulement",
          banking_compliance: "Réglementation Bancaire et Conformité",
          wealth_management: "Gestion de Patrimoine",
          credit_risk_analysis: "Analyse de Crédit et Risque",
          brokerage_investments: "Courtage et Placements",
          corporate_law: "Droit Corporatif et Gouvernance",
          real_estate_law: "Droit Immobilier (Zonage, Titres, Baux Commerciaux)",
          ma_law: "Droit des Fusions & Acquisitions (M&A Law)",
          labor_law: "Droit du Travail et Conventions Collectives",
          business_litigation: "Contentieux d'Affaires",
          ip_law: "Droit de la Propriété Intellectuelle (PI)",
          tax_law: "Droit Fiscal et Structuration",
          immigration_law: "Droit de l'Immigration d'Affaires",
          audit_certification: "Audit et Certification (CPA, CA)",
          tax_planning: "Planification Fiscale et Optimisation",
          tax_compliance: "Conformité Fiscale (Déclarations, Rapports)",
          compliance_audit: "Audit de Conformité Réglementaire (AML, KYC)",
          management_accounting: "Comptabilité de Gestion",
          internal_control: "Contrôle Interne et Gestion des Risques",
          civil_engineering: "Génie Civil & Infrastructure",
          architecture: "Architecture et Planification Urbaine",
          mechanical_engineering: "Génie Mécanique & Matériaux",
          industrial_engineering: "Génie Industriel et Optimisation (Lean/Six Sigma)",
          energy_engineering: "Génie de l'Énergie",
          chemical_engineering: "Génie Chimique & Procédés",
          electrical_engineering: "Génie Électrique & Électronique",
          aerospace_engineering: "Génie Aérospatial & Marine",
          interior_design: "Design Intérieur et Aménagement Commercial",
          building_code_compliance: "Conformité aux Codes du Bâtiment et Normes de Sécurité",
          construction_project_management: "Gestion de Projets d'Infrastructure et de Construction",
          workforce_management: "Gestion de la Main d'Œuvre / RH Spécialisée (Métiers)",
          quality_control: "Contrôle Qualité (CVC, Plomberie, Électricité, Toiture)",
          estimation_bidding: "Estimation et Soumissions",
          site_coordination: "Coordination de Chantier",
          cybersecurity_compliance: "Cyber-Sécurité et Conformité (ISO, SOC 2, RGPD)",
          software_engineering: "Génie Logiciel et Développement Web (DevOps, Full Stack)",
          software_architecture: "Architecture Logicielle (Scalabilité, Cloud)",
          software_patents: "Brevets Logiciels et Protection IP Tech",
          business_intelligence: "Business Intelligence et Analyse de Données",
          it_automation: "Automatisation des Processus IT (RPA)",
          infrastructure_networks: "Infrastructure et Réseaux",
          technical_support: "Support Technique et MSP",
          biomedical_engineering: "Génie Biomédical",
          clinical_research: "Recherche Clinique et Scientifique",
          biological_data_analysis: "Analyse de Données Biologiques (Bio-informatique)",
          health_compliance: "Conformité Réglementaire Santé (FDA, Santé Canada, HIPAA)",
          pharmacovigilance: "Pharmacovigilance et Assurance Qualité Médicale",
          equipment_operations: "Opérations et Maintenance Équipements (Pharma)",
          packaging_line_management: "Gestion Lignes de Conditionnement Pharmaceutique",
          medical_marketing: "Commercialisation Médicale (Délégué Hospitalier)",
          practice_management: "Gestion de Pratique Médicale",
          nursing_care: "Soins Infirmiers et Paramédical",
          post_merger_integration: "Intégration Post-Acquisition (PMI)",
          lean_six_sigma: "Lean Manufacturing / Six Sigma",
          supply_chain_management: "Gestion de la Chaîne Logistique",
          international_transportation: "Transport International et Douanes",
          specialized_logistics: "Logistique Spécialisée (Trucking, Marine, Aviation)",
          industrial_automation: "Automatisation Industrielle (Industry 4.0)",
          inventory_management: "Gestion des Stocks et de l'Inventaire",
          production_optimization: "Optimisation des Opérations de Production",
          warehouse_management: "Gestion d'Entrepôt",
          waste_water_management: "Gestion des Déchets et Traitement de l'Eau",
          environmental_compliance: "Conformité Environnementale",
          impact_assessment: "Évaluation d'Impact Environnemental",
          sustainability_consulting: "Conseil en Développement Durable",
          site_remediation: "Remédiation de Sites Contaminés",
          customer_acquisition: "Stratégie d'Acquisition de Clients (SEO/SEM/Social)",
          marketing_roi_analysis: "Analyse du ROI Marketing et Attributions",
          public_relations: "Relations Publiques et Gestion de Crise",
          brand_strategy: "Stratégie de Marque et Positionnement",
          advertising_campaigns: "Publicité et Campagnes Média",
          content_marketing: "Marketing de Contenu et Rédaction",
          audiovisual_production: "Production Audio-Visuelle",
          media_distribution: "Distribution et Diffusion Média",
          social_media_management: "Gestion des Réseaux Sociaux",
          graphic_design: "Design Graphique et Identité Visuelle",
          advertising_design: "Design Publicitaire et Créatif",
          sales_digital_transformation: "Transformation Numérique des Ventes (Sales Tech)",
          business_development: "Développement des Affaires (B2B/B2C)",
          key_account_management: "Gestion de Comptes Clés",
          ecommerce_platforms: "E-commerce et Plateformes en Ligne",
          omnichannel_strategies: "Stratégies Omnicanales",
          conversion_optimization: "Optimisation des Taux de Conversion (CRO)",
          agricultural_consulting: "Conseil et Formation Agricole/Agroalimentaire",
          biotech_rd: "R&D en Biotechnologie/Agroalimentaire",
          agricultural_development: "Aménagement Agricole et Forestier",
          food_regulation: "Réglementation Alimentaire (HACCP, ACIA)",
          farm_management: "Gestion d'Exploitation Agricole",
          food_processing: "Transformation Alimentaire",
          recruitment_staffing: "Recrutement et Dotation",
          compensation_benefits: "Gestion de la Rémunération et Avantages",
          training_development: "Formation et Développement",
          talent_management: "Gestion des Talents et Successions",
          labor_relations: "Relations de Travail",
          occupational_health: "Santé et Sécurité au Travail (SST)",
          real_estate_appraisal: "Évaluation Immobilière",
          property_management: "Gestion de Propriétés",
          commercial_brokerage: "Courtage Immobilier Commercial",
          real_estate_development: "Développement Immobilier",
          leasing_administration: "Location et Administration",
          curriculum_development: "Développement de Curriculum",
          professional_education: "Formation Professionnelle et Continue",
          specialized_education: "Services Éducatifs Spécialisés",
          school_administration: "Administration Scolaire",
          tutoring_support: "Tutorat et Soutien Académique",
          automotive_repair: "Mécanique et Réparation Automobile",
          bodywork_painting: "Carrosserie et Peinture",
          vehicle_sales: "Vente et Location de Véhicules",
          marine_maintenance: "Entretien et Réparation Marine",
          marina_services: "Services de Marina",
          hairdressing_aesthetics: "Coiffure et Esthétique",
          spa_wellness: "Spa et Bien-être",
          salon_management: "Gestion de Salon/Centre",
          beauty_training: "Formation en Beauté",
          beauty_products_sales: "Vente de Produits de Beauté",
          veterinary_care: "Soins Vétérinaires",
          grooming: "Toilettage",
          daycare_boarding: "Garderie et Pension",
          training_behavior: "Dressage et Comportement",
          mobile_pet_services: "Services Mobiles pour Animaux",
          restaurant_management: "Gestion de Restaurant",
          chef_cooking: "Chef et Cuisine",
          service_experience: "Service et Expérience Client",
          procurement_control: "Approvisionnement et Contrôle des Coûts",
          menu_development: "Concept et Développement de Menu",
          store_management: "Gestion de Magasin",
          visual_merchandising: "Merchandising Visuel",
          pos_management: "Gestion de Point de Vente (POS)",
          procurement_purchasing: "Approvisionnement et Achat",
          customer_service: "Service à la Clientèle",
          hotel_management: "Gestion Hôtelière",
          concierge_services: "Services de Conciergerie",
          event_planning: "Planification d'Événements",
          travel_agency: "Agence de Voyages",
          tourist_attractions: "Attractions et Activités Touristiques",
          sports_event_management: "Gestion d'Événements Sportifs",
          coaching_training: "Coaching et Entraînement",
          facility_management: "Gestion d'Installations Récréatives",
          event_production: "Production d'Événements",
          entertainment_shows: "Animation et Spectacles",
          plumbing_heating: "Plombier (Contracteur) & Systèmes de Chauffage",
          electrical_systems: "Électricien (Contracteur) & Systèmes Électriques",
          hvac_ventilation: "CVC (Contracteur) & Systèmes de Ventilation/Climatisation",
          roofing_siding: "Couvreur (Toiture Contracteur) & Remplacement de Siding",
          window_installation: "Installation & Réparation de Fenêtres (Contracteur)",
          window_cleaning: "Nettoyage de Fenêtres (Contracteur)",
          landscaping_pool: "Aménagement Paysager, Piscine et Entretien Extérieur",
          painting_finishes: "Peintre (Contracteur) & Finitions Intérieures/Extérieures",
          handyman_repairs: "Services de Bricolage (Handyman) & Petites Réparations",
          cleaning_maintenance: "Gestion d'entreprises de Nettoyage et Entretien (Résidentiel/Commercial)",
          distribution_management: "Gestion de Distribution",
          supplier_relations: "Relations Fournisseurs",
          distribution_logistics: "Logistique de Distribution",
          b2b_wholesale: "Vente en Gros B2B",
          translation: "Traduction",
          interpretation: "Interprétation",
          writing: "Rédaction",
          virtual_assistant: "Assistant Virtuel",
          web_research: "Recherche sur le Web",
          database_creation: "Recherches Web et Création de Bases de Données",
        },
        experience_years: {
          "0-2": "0 à 2 ans",
          "3-5": "3 à 5 ans",
          "6-10": "6 à 10 ans",
          "10+": "10 ans et plus",
        },
        jurisdictions: {
          canada: "Canada",
          usa: "États-Unis",
          europe: "Europe (Zone Sélective)",
        },
        compensation: {
          per_hour: "Taux horaire (spécifier ci-dessous)",
          per_project: "Forfait par projet (spécifier ci-dessous)",
          equity_partnership: "Partenariat contre une part de l'équité de la compagnie à acquérir",
        },
        availability: {
          less_10h: "Moins de 10 heures/semaine",
          "10_20h": "10 à 20 heures/semaine",
          more_20h: "Plus de 20 heures/semaine (temps partiel)",
          full_time: "Temps plein (exclusif ou quasi-exclusif)",
          to_be_agreed: "À convenir / sur demande (selon la transaction)",
        },
      },
    },
  },
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(getStoredLanguage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  const contextValue = useMemo(() => ({ language, setLanguage, t }), [language]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
